import {
  MessageText,
  OperationMessageText,
  OperationStateContainer,
  SuccessAnimationContainer,
} from '../BaseQuickActionModal/styles/BaseModal.styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ActionProcessingModalProps } from './@types';
import { ActionResult } from '../QuickActionBar/@types/QuickActionBarTypes';
import BaseQuickActionModal from '../BaseQuickActionModal';
import { BaseQuickActionModalButton } from '../BaseQuickActionModal/@types';
import { CTAIcon } from '../QuickActionBar/components/CTAIcon';
import LoadingHourglassAnimation from '../../assets/animations/loading-hourglass.json';
import Lottie from 'react-lottie';
import SuccessAnimation from '../../assets/animations/success.json';
import Theme from '../../theme';
import { closeQuickActionModal } from '../../redux/actions/modals.actions';
import { useCtaBasedText } from '../QuickActionBar/hooks/useCtaBasedText';
import { useDispatch } from 'react-redux';

export const QuickActionProcessingModal: React.FC<ActionProcessingModalProps> = ({
  action,
  ctaType,
  modalAlignment,
  loadingText,
  errorHeader: _errorHeader,
}) => {
  const [actionResult, setActionResult] = useState<ActionResult | undefined>();
  const [loading, setLoading] = useState<boolean | undefined>();
  const dispatch = useDispatch();

  const executeAction = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const result = await action();
      setActionResult(result);
    } catch (error) {
      const message = (error as Error)?.message;

      setActionResult({
        success: false,
        errorMessage: message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const renderContainerElement = (): React.ReactNode => {
    if (!loading && actionResult?.success) {
      return (
        <OperationStateContainer>
          <SuccessAnimationContainer
            height={160}
            width={160}
            options={{
              animationData: SuccessAnimation,
              loop: false,
            }}
          />
          <OperationMessageText>Success!</OperationMessageText>
        </OperationStateContainer>
      );
    }

    if (!loading && actionResult?.errorMessage) {
      return <MessageText>{actionResult.errorMessage || 'There was an error'}</MessageText>;
    }

    return (
      <OperationStateContainer>
        <Lottie
          height={240}
          width={240}
          style={{ margin: -40 }}
          options={{
            animationData: LoadingHourglassAnimation,
          }}
        />
        <OperationMessageText>{loadingText ?? 'Loading...'}</OperationMessageText>
      </OperationStateContainer>
    );
  };

  const confirmationButton = useMemo((): BaseQuickActionModalButton | undefined => {
    if (actionResult?.errorMessage) {
      return {
        action: (): void => {
          setActionResult(undefined);
          executeAction();
        },
        text: 'Try again',
      };
    }

    return undefined;
  }, [actionResult?.errorMessage]);

  const { errorHeader: ctaErrorHeader } = useCtaBasedText(ctaType);
  const errorHeader = _errorHeader || ctaErrorHeader;

  const closeModal = useCallback(() => {
    dispatch(closeQuickActionModal('quickActionProcessingModal'));
  }, []);

  useEffect(() => {
    executeAction();
  }, []);

  useEffect(() => {
    if (actionResult?.success) {
      setTimeout(() => {
        closeModal();
      }, 4000);
    }
  }, [actionResult?.success]);

  return (
    <BaseQuickActionModal
      header={{
        title: actionResult?.errorMessage && errorHeader,
        icon: (
          <CTAIcon
            type={actionResult?.errorMessage ? 'error' : ctaType}
            color={Theme.colors.quickActionButton.hexCode}
          />
        ),
      }}
      confirmButton={confirmationButton}
      shouldAllowSoftDismissing={!loading}
      modalAlignment={modalAlignment}
    >
      {renderContainerElement()}
    </BaseQuickActionModal>
  );
};
